import type { Query, QueryClient } from '@tanstack/vue-query';
import { getEcho } from '@/services/echo';
import { getAccessToken } from '@/services/tokenStorage';
import type { LiveRecordsMeta } from '@/composables/liveRecords/liveRecords';

/**
 * Fetch again the records on screen whose search documents the backend just rewrote, so a count
 * changed by an edit updates without a page refresh (SAU-530). Spec: tabasco
 * docs/specs/sau-530-live-record-updates.md.
 *
 * The backend broadcasts `SearchDocumentsWritten` with the ids it wrote, on one private channel
 * per kind of record, once a search can see the new documents. A query joins by declaring the
 * records it holds in its `meta` ({@link liveRecords}). This module watches the query cache, joins
 * a kind's channel the first time a query declares it, and refetches the queries that hold an
 * announced id. They fetch by id, so rows update in place and never move.
 *
 * Channels are kept until logout ({@link resetSearchDocumentAnnouncements}). An idle channel costs
 * almost nothing, and leaving on unmount would race with joining again when a screen remounts.
 * The 30-second live poll stays the fallback for anything missed while the socket was down.
 */

/** Announcements inside this window are merged into one refetch per query. */
export const ANNOUNCEMENT_WINDOW_MS = 500;

const ANNOUNCEMENT_EVENT = '.SearchDocumentsWritten';

interface AnnouncementPayload {
    ids?: unknown;
}

/** Kinds of record whose channel this session has joined. */
const joinedSlugs = new Set<string>();

/** Kinds of record whose channel was refused, with the token it was refused. */
const refusedWithToken = new Map<string, string>();

function liveRecordsOf(query: Query): LiveRecordsMeta | undefined {
    return query.meta?.liveRecords;
}

/**
 * Join the live updates for the queries in this client. Returns a function that stops watching;
 * the channels stay joined until {@link resetSearchDocumentAnnouncements}.
 */
export function installSearchDocumentAnnouncements(queryClient: QueryClient): () => void {
    /** slug -> ids announced in the current window. */
    const pending = new Map<string, Set<string>>();
    let windowTimer: ReturnType<typeof setTimeout> | null = null;
    let waitingForVisible = false;

    function refetchPending(): void {
        if (document.visibilityState === 'hidden') {
            // A hidden tab fetches nothing; it catches up the moment it is shown.
            waitingForVisible = true;
            return;
        }
        const announced = new Map(pending);
        pending.clear();
        void queryClient.refetchQueries(
            {
                type: 'active',
                predicate: (query) => {
                    const records = liveRecordsOf(query);
                    const ids = records && announced.get(records.slug);
                    return !!ids && records.ids.some((id) => ids.has(id));
                },
            },
            // A fetch already running may have read the old document; the newest one wins.
            { cancelRefetch: true },
        );
    }

    function receive(slug: string, payload: unknown): void {
        const ids = (payload as AnnouncementPayload | null)?.ids;
        if (!Array.isArray(ids) || ids.length === 0) return;
        const slugIds = pending.get(slug) ?? new Set<string>();
        ids.forEach((id) => slugIds.add(String(id)));
        pending.set(slug, slugIds);
        windowTimer ??= setTimeout(() => {
            windowTimer = null;
            refetchPending();
        }, ANNOUNCEMENT_WINDOW_MS);
    }

    function join(slug: string): void {
        if (joinedSlugs.has(slug)) return;
        // Signed out, or partway through logging out while a screen unmounts. A join now would be
        // refused, and remembered, so the next user to log in on this tab would never join.
        const token = getAccessToken();
        if (token === null) return;
        // The same token would be refused again. A refreshed one is tried.
        if (refusedWithToken.get(slug) === token) return;
        const echo = getEcho();
        if (echo === null) return;
        joinedSlugs.add(slug);
        const channel = `search-documents.${slug}`;
        echo.private(channel)
            .listen(ANNOUNCEMENT_EVENT, (payload: unknown) => receive(slug, payload))
            .error(() => {
                // Refused, for example because the token had expired. Forget the channel, so
                // the next query of this kind joins again once the token has been refreshed.
                joinedSlugs.delete(slug);
                refusedWithToken.set(slug, token);
                echo.leave(channel);
            });
    }

    function onVisibilityChange(): void {
        if (waitingForVisible && document.visibilityState !== 'hidden') {
            waitingForVisible = false;
            refetchPending();
        }
    }

    queryClient.getQueryCache().getAll().forEach((query) => {
        const records = liveRecordsOf(query);
        if (records) join(records.slug);
    });
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
        const records = liveRecordsOf(event.query);
        if (records) join(records.slug);
    });
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
        unsubscribe();
        document.removeEventListener('visibilitychange', onVisibilityChange);
        if (windowTimer !== null) clearTimeout(windowTimer);
        windowTimer = null;
        pending.clear();
    };
}

/**
 * Forget the joined channels. Called on logout, after Echo is disconnected, so the next user
 * joins with their own token and their own permissions.
 */
export function resetSearchDocumentAnnouncements(): void {
    joinedSlugs.clear();
    refusedWithToken.clear();
}
